import React, { useEffect, useState } from 'react';
import { useCompanies } from '../hooks/useCompanies';
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

/**
 * Component to display companies grid from selected database(s)
 */
export function CompaniesGrid({ databaseId, onSelectCompany, onEditCompany, onDeleteCompany, onAsaasPanel }) {
  const { companies, loading, listByDatabase, listConsolidated, total } = useCompanies();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    if (databaseId === 'consolidated') {
      listConsolidated({ search, limit: 100 });
    } else if (databaseId) {
      listByDatabase(databaseId, { search, limit: 100 });
    }
  }, [databaseId, search, listByDatabase, listConsolidated]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedCompanies = [...companies].sort((a, b) => {
    let aVal = a[sortBy] || '';
    let bVal = b[sortBy] || '';

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const getStatusBadge = (status) => {
    return status ? 'Ativo' : 'Inativo';
  };

  const getStatusColor = (status) => {
    return status
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const getPlanName = (company) => {
    if (company.plan?.name) return company.plan.name;
    if (company.planName) return company.planName;
    return '-';
  };

  const getDueDateDisplay = (dueDate) => {
    if (!dueDate) return '-';
    return moment.utc(dueDate).format('DD/MM/YYYY');
  };

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return '';
    const now = moment();
    const due = moment.utc(dueDate);
    const diff = due.diff(now, 'days');
    
    if (diff < 0) return 'expired';
    if (diff <= 5) return 'expiring';
    return '';
  };

  const getDueDateStatusColor = (status) => {
    if (status === 'expired') return 'text-red-600';
    if (status === 'expiring') return 'text-orange-600';
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando empresas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      {/* Search Bar */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar empresas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
        />
      </div>

      {/* Results Count */}
      <div className="text-base text-gray-600">
        Mostrando {sortedCompanies.length} de {total} empresas
      </div>

      {sortedCompanies.length === 0 ? (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-base text-gray-500">Nenhuma empresa encontrada</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <SortHeader field="id" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  ID
                </SortHeader>
                <SortHeader field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Nome da Empresa
                </SortHeader>
                <SortHeader field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Email
                </SortHeader>
                <SortHeader field="phone" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Telefone
                </SortHeader>
                <SortHeader field="plan" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Plano
                </SortHeader>
                <SortHeader field="dueDate" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Vencimento
                </SortHeader>
                <SortHeader field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                  Status
                </SortHeader>
                <th className="px-6 py-4 text-left font-semibold text-gray-700 text-base">Banco de Dados</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-700 text-base">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanies.map((company, idx) => {
                const dueDateStatus = getDueDateStatus(company.dueDate);
                return (
                  <tr
                    key={company.id}
                    className={`transition ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    } hover:bg-blue-50 border-b border-gray-100`}
                  >
                    <td className="px-6 py-4 font-mono text-base text-gray-600">{company.id}</td>
                    <td className="px-6 py-4 font-semibold text-gray-900 text-base">{company.name}</td>
                    <td className="px-6 py-4 text-gray-600 text-base">{company.email || '-'}</td>
                    <td className="px-6 py-4 text-gray-600 text-base">{company.phone || '-'}</td>
                    <td className="px-6 py-4 text-base">
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                        {getPlanName(company)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-base font-semibold ${getDueDateStatusColor(dueDateStatus)}`}>
                      {getDueDateDisplay(company.dueDate)}
                    </td>
                    <td className="px-6 py-4 text-base">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                          company.status
                        )}`}
                      >
                        {getStatusBadge(company.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-base">
                      {company._database?.displayName || company._database?.name || '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => onEditCompany?.(company)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition"
                          title="Editar empresa"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => onAsaasPanel?.(company)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded transition"
                          title="Gerenciar cobranças"
                        >
                          <CreditCardIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => onDeleteCompany?.(company)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded transition"
                          title="Deletar empresa"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Sortable column header
 */
function SortHeader({ field, sortBy, sortOrder, onSort, children }) {
  const isActive = sortBy === field;

  return (
    <th
      onClick={() => onSort(field)}
      className="px-6 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition"
    >
      <div className="flex items-center gap-2">
        {children}
        {isActive && (
          <ChevronUpDownIcon
            className={`w-4 h-4 transition transform ${
              sortOrder === 'desc' ? 'rotate-180' : ''
            }`}
          />
        )}
      </div>
    </th>
  );
}
